import React, { createContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import flashSyncCrypto from '../utils/crypto';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [encryptionKey, setEncryptionKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          api.setAuthToken(token);
          const response = await api.get('/api/auth/me');
          setUser(response.data);
          console.log('[AuthContext] User authenticated successfully');

          const cachedKey = sessionStorage.getItem('flashsync_ek');
          if (cachedKey) {
            try {
              const key = await flashSyncCrypto.importKey(cachedKey);
              setEncryptionKey(key);
              console.log('[AuthContext] Encryption key restored from session cache');
            } catch (err) {
              sessionStorage.removeItem('flashsync_ek');
              console.warn('[AuthContext] Failed to restore cached key:', err);
            }
          }
        } catch (err) {
          console.error('[AuthContext] Auth check failed:', err);
          localStorage.removeItem('token');
          api.setAuthToken(null);
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (email, password, serverUrl) => {
    console.log('[AuthContext] Starting login process for:', email);
    console.log('[AuthContext] Using server URL:', serverUrl);
    setLoading(true);
    setError(null);
    
    try {
      // Store the server URL first
      localStorage.setItem('serverUrl', serverUrl);
      
      // Set the API base URL
      api.setBaseUrl(serverUrl);
      console.log('[AuthContext] API base URL set for login to:', serverUrl);
      
      // Get salt from server instead of localStorage
      console.log('[AuthContext] Retrieving salt from server for:', email);
      const saltResponse = await api.getSalt(email);
      const existingSalt = saltResponse.salt;
      console.log('[AuthContext] Salt retrieved from server successfully');
      
      // Generate proper auth hash using server salt
      const { authHash } = await flashSyncCrypto.generateAuthHash(password, email, existingSalt);
      console.log('[AuthContext] Generated auth hash for login');
      
      const response = await api.post('/api/auth/login', { email, authHash });
      
      if (response.data.token) {
        console.log('[AuthContext] Login successful, storing token and getting user data...');
        
        localStorage.setItem('token', response.data.token);
        api.setAuthToken(response.data.token);
        
        // Get user data
        const userResponse = await api.get('/api/auth/me');
        setUser(userResponse.data);
        console.log('[AuthContext] User data retrieved:', userResponse.data);

        // Derive encryption key for session use
        const saltBuffer = flashSyncCrypto.base64ToArrayBuffer(existingSalt);
        const { key } = await flashSyncCrypto.deriveKeyFromPassword(password, saltBuffer);
        setEncryptionKey(key);
        console.log('[AuthContext] Encryption key derived and stored in session');

        const exportedKey = await flashSyncCrypto.exportKey(key);
        sessionStorage.setItem('flashsync_ek', exportedKey);
        console.log('[AuthContext] Encryption key cached in sessionStorage');
        
        setLoading(false);
        return true;
      }
    } catch (err) {
      console.error('[AuthContext] Login error:', err);
      console.error('[AuthContext] Login error details:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        message: err.message
      });
      setError(err.response?.data?.message || 'Login failed');
      setLoading(false);
      return false;
    }
  };

  const register = async (email, password, serverUrl) => {
    console.log('[AuthContext] Starting registration process for:', email);
    console.log('[AuthContext] Using server URL:', serverUrl);
    setLoading(true);
    setError(null);
    
    try {
      // Store the server URL first
      localStorage.setItem('serverUrl', serverUrl);
      
      // Set the API base URL
      api.setBaseUrl(serverUrl);
      console.log('[AuthContext] API base URL set for registration to:', serverUrl);
      
      // Generate proper auth hash and salt using cryptography
      const { authHash, salt } = await flashSyncCrypto.generateAuthHash(password, email);
      console.log('[AuthContext] Generated auth hash and salt for registration');
      
      // No longer store salt locally - server will handle it
      
      await api.post('/api/auth/register', { 
        email, 
        authHash,
        salt
      });
      
      console.log('[AuthContext] Registration successful');
      setLoading(false);
      return true;
    } catch (err) {
      console.error('[AuthContext] Registration error:', err);
      console.error('[AuthContext] Registration error details:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        message: err.message
      });
      setError(err.response?.data?.message || 'Registration failed');
      setLoading(false);
      return false;
    }
  };

  const logout = () => {
    console.log('[AuthContext] Logging out user');
    localStorage.removeItem('token');
    sessionStorage.removeItem('flashsync_ek');
    api.setAuthToken(null);
    setUser(null);
    setEncryptionKey(null);
    navigate('/login');
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        encryptionKey,
        loading,
        error,
        login,
        register,
        logout,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
